import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AuthLayeredCard from './AuthLayeredCard';
import { authButtonStyle } from '../constants/authStyles';
import { createChatSocket } from '../chat/chatSocket';
import { usePopupMessage } from '../hooks/usePopupMessage';
import { isInvalidTokenError, redirectToLoginForInvalidSession } from '../utils/authSession';
import { formatTime } from '../utils/localeFormat';
import './Auth.css';
import './Battle.css';
import './Village.css';
import './ItemMarket.css';
import './GlobalChat.css';

const MAX_MESSAGE_LENGTH = 50;
const MAX_CHAT_HISTORY = 20;
const COOLDOWN_SECONDS = 10;
const MIN_SEND_LEVEL = 10;

const normalizeMessages = (items) => (Array.isArray(items) ? items.slice(-MAX_CHAT_HISTORY) : []);

const GlobalChat = ({ username }) => {
  const { t } = useTranslation('chat');
  const { t: tc } = useTranslation('common');
  const [messages, setMessages] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [level, setLevel] = useState(null);
  const [canSend, setCanSend] = useState(false);
  const { setError: showChatError, clearError: clearChatError } = usePopupMessage();
  const [cooldown, setCooldown] = useState(0);
  const [isConnected, setIsConnected] = useState(false);
  const socketRef = useRef(null);
  const messagesEndRef = useRef(null);

  const tickerText = useMemo(() => {
    if (messages.length === 0) {
      return t('tickerEmpty');
    }
    return messages
      .slice(-8)
      .map((message) => `${message.username}: ${message.text}`)
      .join('  •  ');
  }, [messages, t]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!username || !token) return;

    const socket = createChatSocket();
    socketRef.current = socket;

    const authenticate = async () => {
      try {
        const response = await socket.send('auth', { token }, 'auth');
        setLevel(response.level);
        setCanSend(Boolean(response.canSend));
        setMessages(normalizeMessages(response.history));
        clearChatError();
      } catch (err) {
        if (isInvalidTokenError(err)) {
          redirectToLoginForInvalidSession();
          return;
        }
        showChatError(err.message || t('errorConnectFailed'));
      }
    };

    const offConnected = socket.on('connected', () => {
      setIsConnected(true);
      authenticate();
    });
    const offDisconnected = socket.on('disconnected', () => {
      setIsConnected(false);
    });
    const offMessage = socket.on('message', (message) => {
      setMessages((current) => normalizeMessages([...current, message]));
    });
    const offError = socket.on('error', (err) => {
      if (isInvalidTokenError(err)) {
        redirectToLoginForInvalidSession();
        return;
      }
      showChatError(err?.message || t('errorConnectionFailed'));
      if (err?.code === 'rate_limit' && err.retryAfterSec) {
        setCooldown(err.retryAfterSec);
      }
    });

    socket.connect();

    return () => {
      offConnected();
      offDisconnected();
      offMessage();
      offError();
      socket.close();
      if (socketRef.current === socket) {
        socketRef.current = null;
      }
    };
  }, [username]);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const timer = setInterval(() => {
      setCooldown((current) => Math.max(0, current - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  useEffect(() => {
    if (isOpen && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [isOpen, messages]);

  const handleOpen = () => {
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
    clearChatError();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const text = inputValue.trim();
    if (!text || cooldown > 0 || !canSend) return;

    try {
      await socketRef.current?.send('send', { text }, 'sent');
      setInputValue('');
      setCooldown(COOLDOWN_SECONDS);
      clearChatError();
    } catch (err) {
      showChatError(err.message || t('errorSendFailed'));
      if (err.code === 'rate_limit' && err.retryAfterSec) {
        setCooldown(err.retryAfterSec);
      }
    }
  };

  const remainingCharacters = MAX_MESSAGE_LENGTH - inputValue.length;
  const isSendDisabled = !inputValue.trim() || cooldown > 0 || !isConnected;

  return (
    <>
      <button type="button" className="global-chat-ticker" onClick={handleOpen}>
        <span className="global-chat-ticker__label">{t('tickerLabel')}</span>
        <span className="global-chat-ticker__viewport">
          <span className="global-chat-ticker__text">{tickerText}</span>
        </span>
      </button>

      {isOpen && (
        <div
          className="popup-overlay item-market-overlay global-chat-overlay"
          onClick={handleClose}
          role="presentation"
        >
          <AuthLayeredCard
            className="item-market-card global-chat-panel"
            onClick={(event) => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div className="item-market-header global-chat-header">
              <div className="item-market-header__info">
                <h2 className="item-market-title">{t('title')}</h2>
                <div className="item-market-balance">
                  {level ? t('level', { level }) : t('levelLoading')}
                  {isConnected ? ` • ${t('connected')}` : ` • ${t('connecting')}`}
                </div>
              </div>
              <button
                type="button"
                className="item-market-close"
                onClick={handleClose}
                aria-label={tc('close')}
              >
                ✕
              </button>
            </div>

            <div className="global-chat-safety" role="note">⚠️ {t('safetyNotice')}</div>

            <div className="item-market-body global-chat-messages">
              {messages.length === 0 ? (
                <div className="item-market-empty global-chat-empty">{t('empty')}</div>
              ) : (
                messages.map((message, index) => (
                  <div className="global-chat-message" key={`${message.at}-${message.username}-${index}`}>
                    <div className="global-chat-message__meta">
                      <span>Lv.{message.level}</span>
                      <strong>{message.username}</strong>
                      <time>{formatTime(message.at, { hour: '2-digit', minute: '2-digit' })}</time>
                    </div>
                    <div className="global-chat-message__text">{message.text}</div>
                  </div>
                ))
              )}
              <div ref={messagesEndRef} />
            </div>

            {canSend ? (
              <form className="global-chat-form global-chat-form--footer" onSubmit={handleSubmit}>
                <input
                  type="text"
                  value={inputValue}
                  maxLength={MAX_MESSAGE_LENGTH}
                  placeholder={cooldown > 0 ? t('cooldownPlaceholder', { sec: cooldown }) : t('placeholder')}
                  disabled={cooldown > 0 || !isConnected}
                  onChange={(event) => setInputValue(event.target.value)}
                />
                <span className="global-chat-counter">{remainingCharacters}</span>
                <button
                  type="submit"
                  className="auth-button global-chat-send"
                  style={authButtonStyle}
                  disabled={isSendDisabled}
                >
                  {t('send')}
                </button>
              </form>
            ) : (
              <div className="global-chat-locked">
                <p>{t('locked', { level: MIN_SEND_LEVEL })}</p>
              </div>
            )}
          </AuthLayeredCard>
        </div>
      )}
    </>
  );
};

export default GlobalChat;
